import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import InputWithLabel from "../../components/form/input/InputWithLabel";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { ServicePauseFormData } from "../../types/servicePause";

export default function ServicePauseForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<ServicePauseFormData>({
    fromDate: "",
    toDate: "",
    fromTime: "",
    toTime: "",
    fromTimeString: "",
    toTimeString: "",
    remarks: "",
    status: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formatDateForInput = (value?: string | null) =>
    value ? String(value).slice(0, 10) : "";

  const formatTimeForInput = (timeString?: string | null, time?: string | null) =>
    timeString || time || "";

  const fetchServicePauseData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/servicepauses/${id}`);
      const data = response.data;
      const fromTime = formatTimeForInput(data.fromTimeString, data.fromTime);
      const toTime = formatTimeForInput(data.toTimeString, data.toTime);
      setFormData({
        fromDate: formatDateForInput(data.fromDate),
        toDate: formatDateForInput(data.toDate),
        fromTime,
        toTime,
        fromTimeString: fromTime,
        toTimeString: toTime,
        remarks: data.remarks || "",
        status: data.status ?? true
      });
    } catch (error) {
      setError("Failed to fetch service pause data");
      console.error("Error fetching service pause:", error);
    }
  }, [id]);

  useEffect(() => {
    if (isEditMode) {
      fetchServicePauseData();
    }
  }, [isEditMode, fetchServicePauseData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const payload = {
        fromDate: formData.fromDate,
        toDate: formData.toDate,
        fromTime: formData.fromTime,
        toTime: formData.toTime,
        fromTimeString: formData.fromTimeString || formData.fromTime,
        toTimeString: formData.toTimeString || formData.toTime,
        remarks: formData.remarks.trim(),
        status: formData.status,
      };

      if (isEditMode) {
        await axiosInstance.put(`/servicepauses/${id}`, payload);
      } else {
        await axiosInstance.post("/servicepauses", payload);
      }
      navigate("/service-pauses");
    } catch (error: any) {
      const message = error.response?.data?.message;
      setError(
        Array.isArray(message)
          ? message.join(", ")
          : message || "Failed to save service pause"
      );
      console.error("Error saving service pause:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleTimeChange = (name: string, value: string) => {
    const timeString = value;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      [`${name}String`]: timeString
    }));
  };

  return (
    <div className="w-full space-y-6">
      <PageBreadcrumb pageTitle={isEditMode ? "Edit Service Pause" : "Add Service Pause"} />

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        {error && (
          <div className="mb-4 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            <InputWithLabel
              label="From Date"
              name="fromDate"
              type="date"
              value={formData.fromDate}
              onChange={handleInputChange}
              required
            />

            <InputWithLabel
              label="To Date"
              name="toDate"
              type="date"
              value={formData.toDate}
              onChange={handleInputChange}
              required
            />

            <InputWithLabel
              label="From Time"
              name="fromTime"
              type="time"
              value={formData.fromTime}
              onChange={(e) => handleTimeChange('fromTime', e.target.value)}
              required
            />

            <InputWithLabel
              label="To Time"
              name="toTime"
              type="time"
              value={formData.toTime}
              onChange={(e) => handleTimeChange('toTime', e.target.value)}
              required
            />

            <div className="md:col-span-2">
              <InputWithLabel
                label="Remarks"
                name="remarks"
                value={formData.remarks}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="status"
                name="status"
                checked={formData.status}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="status" className="text-sm font-medium text-gray-700">
                Active
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/service-pauses")}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 